/**
 * 🏋️🌿 Interactive Gym Phase 2 — Real-World Holistic Session UI
 *
 * Ships Modules 3 (AI Coach text), 4 (Accessible Exercise Filters),
 * 5 (Breathing Pacer + 2–200 min Rest Timer), and the Steady Rhythm
 * badge (server-computed, mint-toned, zero-shame).
 *
 * Deferred:
 *   • M1 Camera Scan          — needs MediaPipe playbook
 *   • M2 Mirror-Match Avatar  — horseblind rule (visual style deferred)
 *   • M6 Overdrive Protocol   — needs owner numeric drops
 *
 * Canon:
 *   /app/memory/INTERACTIVE_GYM_PHASE2_MASTER_CANON_FEB2026.md
 *   /app/memory/OMNIMUSCLE_HOLISTIC_HEALING_PLATFORM_CANON_FEB2026.md
 *
 * TONE GATE: No gym-bro. Warm, empathetic, educational, radical
 * patience. Same-Muscle-Different-Path: seated band row === heavy
 * free-weight row. Partial-ROM counts.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CoachAudioButton from "@/components/CoachAudioButton";
import { InteractiveARShell } from "@/components/InteractiveARShell";
import { ProgramPlanner } from "@/components/ProgramPlanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTENT_LABEL = {
  UPPER_BODY_PULL: "🎗️ Upper Body Pull",
  UPPER_BODY_PUSH: "🏋️ Upper Body Push",
  LOWER_BODY_PULL: "🌉 Lower Body Pull",
  LOWER_BODY_PUSH: "🦵 Lower Body Push",
  CORE_STABILIZATION: "🪷 Core Stabilization",
  MOBILITY: "🌀 Mobility",
  CARDIO_LOW_IMPACT: "🚶 Low-Impact Cardio",
};
const TAG_LABEL = {
  SEATED_ALTERNATIVE: "🪑 Seated Alternative",
  LOW_IMPACT: "🌊 Low-Impact",
  JOINT_FRIENDLY: "🦴 Joint-Friendly",
  PARTIAL_ROM_VALIDATED: "🎯 Partial-ROM Counts",
  STRAP_OR_CUFF_FRIENDLY: "🎗️ Strap/Cuff Friendly",
  BAND_ALTERNATIVE: "➰ Band Alternative",
  ONE_LIMB_FRIENDLY: "🖐 One-Limb Friendly",
  NO_FLOOR_TRANSITION: "⬆️ No Floor Transition",
};

const STATE_LABEL = {
  warm_up: "🌅 Warm-Up",
  active: "💪 Working Set",
  rest: "🕰 Rest",
  cooldown: "🧘 Cooldown",
  complete: "✅ Complete",
};

function fmtElapsed(seconds) {
  const s = Math.max(0, Number(seconds) || 0);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

export default function GymPhase2Page() {
  const [catalog, setCatalog] = useState(null);
  const [pacers, setPacers] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [loadingExercises, setLoadingExercises] = useState(true);

  const [selectedIntents, setSelectedIntents] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);

  const [greeting, setGreeting] = useState(null);
  const [pickedExercise, setPickedExercise] = useState(null);
  const [pacer, setPacer] = useState("lift_rhythm");
  const [plannedMinutes, setPlannedMinutes] = useState(30);
  const [session, setSession] = useState(null);
  const [tickElapsed, setTickElapsed] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const [c, p] = await Promise.all([
          axios.get(`${API}/interactive-gym/catalog`),
          axios.get(`${API}/interactive-gym/pacers`),
        ]);
        setCatalog(c.data);
        setPacers(p.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load gym catalog.");
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoadingExercises(true);
      try {
        const params = new URLSearchParams();
        if (selectedIntents.length) params.set("intents", selectedIntents.join(","));
        if (selectedTags.length) params.set("tags", selectedTags.join(","));
        const r = await axios.get(`${API}/interactive-gym/exercises?${params.toString()}`);
        setExercises(r.data?.exercises || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load exercises.");
      } finally {
        setLoadingExercises(false);
      }
    })();
  }, [selectedIntents, selectedTags]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.post(`${API}/interactive-gym/coach/greeting`, {
          days_since_last_session: null,
          injury_flags: [],
          dexterity_flags: [],
          dial_back: false,
        });
        setGreeting(r.data);
      } catch (_) { /* silent */ }
    })();
  }, []);

  useEffect(() => {
    if (!session || session.state === "complete") return;
    const t = setInterval(() => setTickElapsed((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, [session]);

  const currentElapsed = useMemo(() => {
    if (!session) return 0;
    return (session.elapsed_seconds || 0) + tickElapsed;
  }, [session, tickElapsed]);

  const toggle = (arr, setArr, key) => {
    setArr(arr.includes(key) ? arr.filter((k) => k !== key) : [...arr, key]);
  };

  const startSession = async () => {
    if (!pickedExercise) {
      toast.info("Pick an exercise first — or start a blank session below.");
      return;
    }
    try {
      const r = await axios.post(`${API}/interactive-gym/sessions`, {
        exercise_id: pickedExercise.exercise_id,
        pacer,
        planned_minutes: plannedMinutes,
      });
      setSession(r.data);
      setTickElapsed(0);
      toast.success("Session started — take your time.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start session.");
    }
  };

  const startBlank = async () => {
    try {
      const r = await axios.post(`${API}/interactive-gym/sessions`, {
        exercise_id: null,
        pacer,
        planned_minutes: plannedMinutes,
      });
      setSession(r.data);
      setTickElapsed(0);
      toast.success("Free-form session started.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start session.");
    }
  };

  const transition = async (new_state) => {
    if (!session) return;
    try {
      const r = await axios.post(
        `${API}/interactive-gym/sessions/${session.session_id}/touch`,
        { new_state }
      );
      setSession(r.data);
      setTickElapsed(0);
      if (new_state === "complete") {
        toast.success("Session complete. Great job today.");
        try {
          const g = await axios.post(`${API}/interactive-gym/coach/greeting`, {
            days_since_last_session: 0,
            injury_flags: [],
            dexterity_flags: [],
            dial_back: false,
          });
          setGreeting(g.data);
        } catch (_) { /* silent */ }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update session.");
    }
  };

  const pacerProfile = pacers?.pacers?.[pacer];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-black via-neutral-950 to-black text-white" data-testid="gym-phase2-page">
      <Header />
      <InteractiveARShell space="gym" fallbackGradient="bg-gradient-to-b from-black via-sky-950/20 to-black">
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 py-6 space-y-8">
        {/* HERO */}
        <section className="relative overflow-hidden rounded-2xl border border-neutral-800 bg-gradient-to-br from-sky-950/40 via-black to-neutral-950 p-6" data-testid="gym-phase2-hero">
          <div className="text-[10px] uppercase tracking-widest text-sky-300/80">
            Interactive Gym · Phase 2 · Holistic Healing
          </div>
          <div className="mt-1 text-3xl sm:text-5xl font-black tracking-tight" style={{ fontFamily: "Anton, sans-serif" }}>
            YOUR SESSION. <span className="text-sky-300">YOUR PACE.</span>
          </div>
          <div className="mt-2 text-sm text-neutral-300 max-w-2xl leading-relaxed">
            No shame, no rush, no failure state. Seated band rows count the same as
            heavy free-weight rows. Partial-ROM and static holds count fully. Rest
            between sets as long as you need — the timer holds up to 200 minutes
            without a peep.
          </div>
        </section>

        {/* 🗓️ SELF WORKOUT + FOOD PROGRAM PLANNER — Today/Week/Cycle w/ Food & Workout tabs */}
        <section data-testid="gym-program-section">
          <ProgramPlanner />
        </section>

        {/* AI COACH GREETING (M3) */}
        {greeting && (
          <section className="rounded-xl border border-sky-500/30 bg-gradient-to-br from-sky-950/40 via-black to-black p-5" data-testid="gym-coach-greeting">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="text-[10px] uppercase tracking-widest text-sky-300">
                🧠 AI Coach · Tone-Gated (No Gym-Bro)
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <CoachAudioButton
                  greetingLines={greeting.lines}
                  domain="gym"
                  data-testid="coach-audio-btn"
                />
                {greeting.steady_rhythm?.badge_visible && (
                <div
                  data-testid="steady-rhythm-badge"
                  className="flex items-center gap-1.5 rounded-full px-2.5 py-1 border border-emerald-400/50 bg-gradient-to-r from-emerald-400/20 to-teal-400/20 backdrop-blur-sm"
                  title={`${greeting.steady_rhythm.count} sessions in a steady rhythm`}
                >
                  <span className="text-sm">🌱</span>
                  <span className="text-[10px] font-black uppercase tracking-widest text-emerald-200">
                    Steady Rhythm · {greeting.steady_rhythm.count}
                  </span>
                </div>
              )}
              {greeting.full_circle?.active && (
                <div
                  data-testid="full-circle-badge"
                  className="flex items-center gap-1.5 rounded-full px-2.5 py-1 border border-emerald-300/60 bg-gradient-to-r from-emerald-500/30 to-sky-400/30 backdrop-blur-sm"
                  title={`Kitchen ${greeting.full_circle.kitchen_streak} · Gym ${greeting.full_circle.gym_streak} — both sides steady`}
                >
                  <span className="text-sm">🌿</span>
                  <span className="text-[10px] font-black uppercase tracking-widest text-emerald-100">
                    Full Circle
                  </span>
                </div>
              )}
              </div>
            </div>
            <div className="space-y-2">
              {greeting.lines?.map((line, i) => (
                <div key={i} className="text-sm text-neutral-200 leading-relaxed italic" data-testid={`coach-line-${i}`}>
                  &ldquo;{line}&rdquo;
                </div>
              ))}
              {greeting.full_circle?.active && greeting.full_circle.line && (
                <div className="mt-3 rounded-lg border border-emerald-300/40 bg-gradient-to-r from-emerald-900/30 to-sky-900/30 px-3 py-2" data-testid="full-circle-line">
                  <div className="text-[9px] uppercase tracking-widest text-emerald-300 mb-0.5">🌿 Full Circle</div>
                  <div className="text-sm text-emerald-100 italic leading-relaxed">
                    &ldquo;{greeting.full_circle.line}&rdquo;
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ACTIVE SESSION OR LAUNCHER (M5) */}
        {session ? (
          <SessionPanel
            session={session}
            pacerProfile={pacerProfile}
            elapsed={currentElapsed}
            onTransition={transition}
            onClose={() => { setSession(null); setTickElapsed(0); }}
          />
        ) : (
          <section className="rounded-xl border border-neutral-800 bg-black/40 p-5 space-y-4" data-testid="session-launcher">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">
              🌬️ Session Launcher · Breathing Pacer + 2–200 min Rest Timer
            </div>

            <div>
              <div className="text-xs text-neutral-400 mb-2">Choose a breathing rhythm</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {pacers && Object.entries(pacers.pacers).map(([key, p]) => {
                  const active = pacer === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setPacer(key)}
                      data-testid={`pacer-btn-${key}`}
                      className={`text-left rounded-xl p-3 border transition ${
                        active
                          ? "bg-gradient-to-br from-sky-700 to-blue-800 border-transparent ring-2 ring-sky-400/60"
                          : "bg-black/50 border-neutral-800 hover:border-neutral-600"
                      }`}
                    >
                      <div className="text-sm font-black text-white" style={{ fontFamily: "Anton, sans-serif" }}>
                        {p.label}
                      </div>
                      <div className="text-[10px] text-neutral-300 mt-1 italic leading-snug">
                        {p.cue_in}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between text-xs text-neutral-400 mb-1">
                <span>Planned time — you can always go longer</span>
                <span className="text-sky-300 font-black" data-testid="planned-minutes">
                  {plannedMinutes} min
                </span>
              </div>
              <input
                type="range"
                min={catalog?.min_minutes || 2}
                max={catalog?.max_minutes || 200}
                value={plannedMinutes}
                onChange={(e) => setPlannedMinutes(Number(e.target.value))}
                className="w-full accent-sky-500"
                data-testid="planned-minutes-slider"
              />
              <div className="text-[10px] text-neutral-500 italic mt-1">
                Range: 2 to 200 minutes. The timer never expires on you — walk away and come back.
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-1">
              <button
                onClick={startSession}
                disabled={!pickedExercise}
                data-testid="start-exercise-session-btn"
                className={`text-xs font-black uppercase tracking-widest px-4 py-2 rounded-full transition ${
                  pickedExercise
                    ? "bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-400 hover:to-blue-500 text-black"
                    : "bg-neutral-900 text-neutral-600 cursor-not-allowed border border-neutral-800"
                }`}
              >
                {pickedExercise ? `Start · ${pickedExercise.name}` : "Pick an exercise below"}
              </button>
              <button
                onClick={startBlank}
                data-testid="start-blank-session-btn"
                className="text-xs font-black uppercase tracking-widest px-4 py-2 rounded-full border border-neutral-700 text-neutral-200 hover:border-sky-500 hover:text-sky-200 transition"
              >
                Or start a free-form session
              </button>
            </div>
          </section>
        )}

        {/* ACCESSIBILITY FILTERS (M4) */}
        <section className="rounded-xl border border-neutral-800 bg-black/30 p-5 space-y-4" data-testid="accessibility-filters">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">
            ♿🌱 Filter By What Works For You Today
          </div>

          <div>
            <div className="text-xs text-neutral-400 mb-2">Functional intent</div>
            <div className="flex flex-wrap gap-1.5">
              {(catalog?.intents || []).map((i) => {
                const active = selectedIntents.includes(i);
                return (
                  <button
                    key={i}
                    onClick={() => toggle(selectedIntents, setSelectedIntents, i)}
                    data-testid={`intent-chip-${i}`}
                    className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border transition ${
                      active
                        ? "bg-sky-600 text-black border-transparent"
                        : "bg-neutral-950 text-neutral-300 border-neutral-800 hover:border-sky-500"
                    }`}
                  >
                    {INTENT_LABEL[i] || i}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="text-xs text-neutral-400 mb-2">Accessibility & convenience</div>
            <div className="flex flex-wrap gap-1.5">
              {(catalog?.tags || []).map((t) => {
                const active = selectedTags.includes(t);
                return (
                  <button
                    key={t}
                    onClick={() => toggle(selectedTags, setSelectedTags, t)}
                    data-testid={`tag-chip-${t}`}
                    className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border transition ${
                      active
                        ? "bg-emerald-600 text-black border-transparent"
                        : "bg-neutral-950 text-neutral-300 border-neutral-800 hover:border-emerald-500"
                    }`}
                  >
                    {TAG_LABEL[t] || t}
                  </button>
                );
              })}
            </div>
          </div>

          {(selectedIntents.length > 0 || selectedTags.length > 0) && (
            <button
              onClick={() => { setSelectedIntents([]); setSelectedTags([]); }}
              data-testid="clear-filters-btn"
              className="text-[10px] uppercase tracking-widest text-neutral-500 hover:text-white transition"
            >
              Clear all filters
            </button>
          )}
        </section>

        {/* EXERCISE GRID (M4) */}
        <section data-testid="exercise-grid-section">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
            {loadingExercises
              ? "Loading exercises…"
              : `${exercises.length} exercise${exercises.length === 1 ? "" : "s"} — Same-Muscle-Different-Path`}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="exercise-grid">
            {exercises.map((r) => (
              <ExerciseCard
                key={r.exercise_id}
                exercise={r}
                active={pickedExercise?.exercise_id === r.exercise_id}
                onPick={() => setPickedExercise(r)}
              />
            ))}
            {!loadingExercises && exercises.length === 0 && (
              <div className="col-span-full text-center py-8 text-neutral-500 italic border border-dashed border-neutral-800 rounded-xl" data-testid="no-exercises-empty-state">
                No exercises match those filters — try loosening one, or clear filters. No wrong choice here.
              </div>
            )}
          </div>
        </section>
      </main>
      </InteractiveARShell>
      <Footer />
    </div>
  );
}

/* ─────────────────────────── Exercise Card ─────────────────────────── */
function ExerciseCard({ exercise, active, onPick }) {
  return (
    <button
      onClick={onPick}
      data-testid={`exercise-card-${exercise.exercise_id}`}
      className={`text-left rounded-xl p-4 border transition ${
        active
          ? "border-sky-500 ring-2 ring-sky-500/40 bg-gradient-to-br from-sky-950/60 to-black"
          : "border-neutral-800 hover:border-sky-500/60 bg-black/40"
      }`}
    >
      <div className="text-3xl mb-2">{exercise.hero_emoji || "🏋️"}</div>
      <div className="text-base font-black text-white" style={{ fontFamily: "Anton, sans-serif" }}>
        {exercise.name}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">
        {(exercise.primary_muscles || []).slice(0, 3).join(" · ")}
      </div>
      <div className="mt-2 text-xs text-neutral-300 leading-snug">{exercise.one_line}</div>
      <div className="mt-3 flex flex-wrap gap-1">
        {(exercise.tags || []).slice(0, 4).map((t) => (
          <span key={t} className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full border border-emerald-600/40 bg-emerald-950/40 text-emerald-200">
            {TAG_LABEL[t] || t}
          </span>
        ))}
      </div>
      <div className="mt-2 text-[10px] text-sky-300/80 italic leading-snug">
        {exercise.why_it_works}
      </div>
    </button>
  );
}

/* ─────────────────────────── Active Session Panel ─────────────────────────── */
function SessionPanel({ session, pacerProfile, elapsed, onTransition, onClose }) {
  const [breathPhase, setBreathPhase] = useState("in");
  useEffect(() => {
    if (!pacerProfile) return;
    const cycle = () => {
      setBreathPhase("in");
      const t1 = setTimeout(() => setBreathPhase("out"), pacerProfile.inhale_ms + (pacerProfile.hold_ms || 0));
      return () => clearTimeout(t1);
    };
    cycle();
    const interval = setInterval(cycle, pacerProfile.inhale_ms + pacerProfile.exhale_ms + (pacerProfile.hold_ms || 0) * 2);
    return () => clearInterval(interval);
  }, [pacerProfile]);

  const stateOrder = ["warm_up", "active", "rest", "cooldown", "complete"];
  const isComplete = session.state === "complete";
  const planned = session.planned_minutes;
  const elapsedMin = Math.floor(elapsed / 60);
  const overtime = elapsedMin > planned;

  return (
    <section className="rounded-2xl border border-sky-500/30 bg-gradient-to-br from-sky-950/40 via-black to-black p-6 space-y-5" data-testid="active-session-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-sky-300">
            Active Session
          </div>
          <div className="text-2xl font-black text-white" style={{ fontFamily: "Anton, sans-serif" }} data-testid="session-state-label">
            {STATE_LABEL[session.state] || session.state}
          </div>
        </div>
        <button
          onClick={onClose}
          data-testid="close-session-panel-btn"
          className="text-[10px] uppercase tracking-widest text-neutral-400 hover:text-white transition"
        >
          Close
        </button>
      </div>

      {pacerProfile && (
        <div className="flex items-center gap-4">
          <div
            className={`w-24 h-24 rounded-full border-2 border-sky-400/60 flex items-center justify-center transition-transform duration-[3000ms] ease-in-out ${
              breathPhase === "in" ? "scale-110 bg-sky-500/20" : "scale-90 bg-sky-500/5"
            }`}
            data-testid="breath-pacer-ring"
          >
            <span className="text-xs font-black uppercase tracking-widest text-sky-300">
              {breathPhase === "in" ? "Breathe In" : "Breathe Out"}
            </span>
          </div>
          <div className="text-xs text-neutral-300 italic leading-relaxed">
            <div>{breathPhase === "in" ? pacerProfile.cue_in : pacerProfile.cue_out}</div>
            <div className="mt-1 text-[10px] text-neutral-500 uppercase tracking-widest">
              {pacerProfile.label}
            </div>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-neutral-800 bg-black/50 p-4">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500">Elapsed</div>
        <div className="text-4xl font-black text-white tabular-nums" data-testid="session-elapsed">
          {fmtElapsed(elapsed)}
        </div>
        <div className="text-[10px] text-neutral-500 uppercase tracking-widest mt-1">
          Planned: {planned} min · Max: 200 min
        </div>
        {overtime && (
          <div className="mt-2 text-xs text-sky-300 italic" data-testid="overtime-normalize">
            Overtime is fine — take all the time you need. The app doesn&apos;t expire on you.
          </div>
        )}
      </div>

      {!isComplete && (
        <div className="flex flex-wrap gap-2">
          {stateOrder.filter((s) => s !== session.state).map((s) => (
            <button
              key={s}
              onClick={() => onTransition(s)}
              data-testid={`transition-to-${s}`}
              className="text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border border-sky-500/40 text-sky-200 hover:bg-sky-500 hover:text-black transition"
            >
              → {STATE_LABEL[s] || s}
            </button>
          ))}
        </div>
      )}

      {isComplete && (
        <div className="rounded-xl border border-sky-500/40 bg-sky-900/20 p-4" data-testid="session-complete-banner">
          <div className="text-sm font-black text-sky-300">Complete · Great job today.</div>
          <div className="text-xs text-neutral-300 mt-1 italic">
            Every path to the same muscle counts. Partial-ROM is a full win.
          </div>
        </div>
      )}
    </section>
  );
}
